import type { INodeDto } from '@/types/INodeDto';

/**
 * Extracts the parent path from a SQL Server HierarchyId string.
 * e.g., "/1/2/3/" -> "/1/2/", "/1/" -> undefined (root)
 */
function getParentNodePath(nodePath: string | null | undefined): string | undefined {
  if (!nodePath || nodePath === '/') return undefined;

  // Remove trailing slash and split
  const trimmed = nodePath.endsWith('/') ? nodePath.slice(0, -1) : nodePath;
  const parts = trimmed.split('/').filter(p => p !== '');

  if (parts.length <= 1) {
    // This is a root node (e.g., "/1/")
    return undefined;
  }

  // Remove last segment and rebuild path
  parts.pop();
  return '/' + parts.join('/') + '/';
}

/**
 * Represents a tree structure of nodes (INodeDto) for a single model.
 * The tree is built using indices into a shared node array.
 */
export class InstanceTree {
  /** The modelId this tree represents */
  readonly modelId: string;

  /** Reference to the shared node array */
  private nodes: INodeDto[];

  /** Map from nodeId to node index for fast lookup */
  private nodeIdToIndex: Map<bigint, number> = new Map();

  /** Map from node index to array of child indices */
  private childrenMap: Map<number, number[]> = new Map();

  /** Map from node index to parent index (-1 if root) */
  private parentMap: Map<number, number> = new Map();

  /** Indices of root nodes (nodes without parents) */
  private rootIndices: number[] = [];

  /** All indices belonging to this model */
  private nodeIndices: number[] = [];

  constructor(modelId: string, nodes: INodeDto[]) {
    this.modelId = modelId;
    this.nodes = nodes;
  }

  /**
   * Builds the tree structure from the node array.
   * Filters nodes by modelId and establishes parent-child relationships
   * using NodePath (SQL Server HierarchyId format like /1/2/3/).
   * NodePath is globally unique across models, avoiding collisions with cadReferenceId.
   */
  buildTree(): void {
    // Clear existing data
    this.nodeIdToIndex.clear();
    this.childrenMap.clear();
    this.parentMap.clear();
    this.rootIndices = [];
    this.nodeIndices = [];

    // Map from NodePath to index for parent-child relationship building
    const nodePathToIndex = new Map<string, number>();

    // First pass: Find all nodes for this model and build lookup indices
    for (let i = 0; i < this.nodes.length; i++) {
      const node = this.nodes[i];
      if (node.modelId === this.modelId) {
        this.nodeIndices.push(i);
        // Build nodeId index for fast lookup (nodeId is always set)
        const nid = node.nodeId!;
        const bigId = typeof nid === 'bigint' ? nid : BigInt(nid);
        this.nodeIdToIndex.set(bigId, i);
        // Build NodePath index for parent-child relationship building
        if (node.nodePath) {
          nodePathToIndex.set(node.nodePath, i);
        }
        // Initialize empty children array for each node
        this.childrenMap.set(i, []);
      }
    }

    // Second pass: Build parent-child relationships using NodePath
    for (const index of this.nodeIndices) {
      const node = this.nodes[index];
      const parentPath = getParentNodePath(node.nodePath);

      if (parentPath && nodePathToIndex.has(parentPath)) {
        // This node has a parent
        const parentIndex = nodePathToIndex.get(parentPath)!;
        this.parentMap.set(index, parentIndex);

        // Add this node as a child of the parent
        const children = this.childrenMap.get(parentIndex) || [];
        children.push(index);
        this.childrenMap.set(parentIndex, children);
      } else {
        // This is a root node (no parent path or parent not found)
        this.parentMap.set(index, -1);
        this.rootIndices.push(index);
      }
    }
  }

  /**
   * Gets the node at the specified index.
   */
  getNode(index: number): INodeDto | null {
    if (index >= 0 && index < this.nodes.length) {
      return this.nodes[index];
    }
    return null;
  }

  /**
   * Gets the index of a node by its nodeId.
   */
  getIndexByNodeId(nodeId: bigint): number | undefined {
    return this.nodeIdToIndex.get(nodeId);
  }

  /**
   * Gets all root node indices (nodes without parents).
   */
  getRootIndices(): readonly number[] {
    return this.rootIndices;
  }

  /**
   * Gets all node indices belonging to this model.
   */
  getNodeIndices(): readonly number[] {
    return this.nodeIndices;
  }

  /**
   * Gets the number of nodes in this model.
   */
  getNodeCount(): number {
    return this.nodeIndices.length;
  }

  /**
   * Gets the child indices for a given node index.
   */
  getChildIndices(index: number): readonly number[] {
    return this.childrenMap.get(index) || [];
  }

  /**
   * Gets the parent index for a given node index.
   * Returns -1 if the node is a root node.
   */
  getParentIndex(index: number): number {
    return this.parentMap.get(index) ?? -1;
  }

  /**
   * Checks if a node has children.
   */
  hasChildren(index: number): boolean {
    const children = this.childrenMap.get(index);
    return children !== undefined && children.length > 0;
  }

  /**
   * Checks if a node is a root node.
   */
  isRoot(index: number): boolean {
    return this.parentMap.get(index) === -1;
  }

  /**
   * Traverses the tree depth-first starting from the given index.
   * If no index is provided, traverses from all root nodes.
   */
  traverseDepthFirst(
    callback: (index: number, depth: number) => void,
    startIndex?: number
  ): void {
    const traverse = (index: number, depth: number) => {
      callback(index, depth);
      const children = this.getChildIndices(index);
      for (const childIndex of children) {
        traverse(childIndex, depth + 1);
      }
    };

    if (startIndex !== undefined) {
      traverse(startIndex, 0);
    } else {
      for (const rootIndex of this.rootIndices) {
        traverse(rootIndex, 0);
      }
    }
  }

  /**
   * Traverses the tree breadth-first starting from the given index.
   * If no index is provided, traverses from all root nodes.
   */
  traverseBreadthFirst(
    callback: (index: number, depth: number) => void,
    startIndex?: number
  ): void {
    const queue: Array<{ index: number; depth: number; }> = [];

    if (startIndex !== undefined) {
      queue.push({ index: startIndex, depth: 0 });
    } else {
      for (const rootIndex of this.rootIndices) {
        queue.push({ index: rootIndex, depth: 0 });
      }
    }

    while (queue.length > 0) {
      const { index, depth } = queue.shift()!;
      callback(index, depth);

      const children = this.getChildIndices(index);
      for (const childIndex of children) {
        queue.push({ index: childIndex, depth: depth + 1 });
      }
    }
  }

  /**
   * Gets the path from a node to the root (array of indices).
   */
  getPathToRoot(index: number): number[] {
    const path: number[] = [];
    let current = index;

    while (current !== -1) {
      path.push(current);
      current = this.getParentIndex(current);
    }

    return path;
  }

  /**
   * Gets the depth of a node in the tree.
   */
  getDepth(index: number): number {
    return this.getPathToRoot(index).length - 1;
  }
}

